/**
 * Practice info — the DC's "Prepared by" line and optional website. Persists
 * to localStorage so a returning DC doesn't re-enter it on every visit.
 *
 * Architecture: NEVER LEAVES THE BROWSER. No webhook, no analytics, no server
 * write. Cloudflare logs see HTTP requests, not form values. PHI-safe by
 * design.
 *
 * Schema is versioned (`v1`) — when the shape ever changes we can detect old
 * values and migrate. For v1, breakage in either direction (corrupt JSON,
 * missing field, wrong type) clears the slot and the DC re-enters once.
 */

const STORAGE_KEY = "cvai-tools.practiceInfo.v1";

export interface PracticeInfo {
  /**
   * Free-form practitioner line. DCs format however they like:
   * "Dr. Sarah Chen, DC · Bright Roots Pediatric" or
   * "Dr. Mike Smith — Center for Pediatric Wellness".
   *
   * Required. Empty/whitespace-only is rejected on save.
   */
  practiceLine: string;
  /**
   * Optional. May be a domain ("brightrootschiro.com"), a full URL, or even
   * "Bright Roots on Instagram" — we don't validate format. If empty, the PDF
   * footer omits the trailing "· website" segment.
   */
  practiceWebsite: string;
}

export type LoadResult =
  | { kind: "ok"; info: PracticeInfo }
  | { kind: "empty" } // no value stored
  | { kind: "cleared"; reason: "corrupt-json" | "schema-mismatch" };

export type SaveResult =
  | { kind: "ok" }
  | { kind: "invalid"; field: keyof PracticeInfo; reason: string }
  | { kind: "quota-exceeded" }
  | { kind: "storage-unavailable" };

// -----------------------------------------------------------------------------
// Validation

function isStringField(value: unknown): value is string {
  return typeof value === "string";
}

function looksLikePracticeInfo(value: unknown): value is PracticeInfo {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return isStringField(obj.practiceLine) && isStringField(obj.practiceWebsite);
}

// -----------------------------------------------------------------------------
// Storage access

/**
 * Returns the localStorage object, or null if unavailable.
 *
 * Handles three failure modes:
 *   1. SSR / no window (probably not relevant for cvai-tools, but cheap to guard)
 *   2. Safari private mode where `localStorage` exists but every write throws
 *   3. Browsers with all storage disabled (some hardened/kiosk setups)
 *
 * The probe write+read is necessary because Safari private mode's
 * localStorage object is present but unusable.
 */
function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    // The probe write+read is the actual usability test — Safari private mode
    // exposes a localStorage object whose every write throws. The TypeScript
    // type says `Storage`, but at runtime any access can fail; we don't
    // pre-check existence, we just probe.
    const probeKey = "__cvai-tools-probe__";
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Public API

/**
 * Read practice info from localStorage. Self-heals on corruption: any
 * unparseable JSON, missing field, or wrong type clears the slot and returns
 * `cleared` with the reason — caller treats that the same as `empty`.
 */
export function loadPracticeInfo(): LoadResult {
  const storage = getStorage();
  if (!storage) return { kind: "empty" };

  const raw = storage.getItem(STORAGE_KEY);
  if (raw === null) return { kind: "empty" };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    storage.removeItem(STORAGE_KEY);
    return { kind: "cleared", reason: "corrupt-json" };
  }

  if (!looksLikePracticeInfo(parsed)) {
    storage.removeItem(STORAGE_KEY);
    return { kind: "cleared", reason: "schema-mismatch" };
  }

  return { kind: "ok", info: parsed };
}

/**
 * Persist practice info. Validates required fields before writing.
 * Returns a result discriminator instead of throwing — caller decides UX.
 */
export function savePracticeInfo(info: PracticeInfo): SaveResult {
  if (info.practiceLine.trim() === "") {
    return { kind: "invalid", field: "practiceLine", reason: "Practice line is required" };
  }

  const storage = getStorage();
  if (!storage) return { kind: "storage-unavailable" };

  const sanitized: PracticeInfo = {
    practiceLine: info.practiceLine.trim(),
    practiceWebsite: info.practiceWebsite.trim(),
  };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
    return { kind: "ok" };
  } catch (err) {
    // Quota errors come back as DOMException with name "QuotaExceededError"
    // (Chrome/Edge/Firefox) or "NS_ERROR_DOM_QUOTA_REACHED" (older Firefox).
    if (err instanceof DOMException && /quota/i.test(err.name)) {
      return { kind: "quota-exceeded" };
    }
    return { kind: "storage-unavailable" };
  }
}

/** Remove the stored practice info. Idempotent — safe to call when nothing's there. */
export function clearPracticeInfo(): void {
  const storage = getStorage();
  if (!storage) return;
  storage.removeItem(STORAGE_KEY);
}

/** Quick predicate for the soft-gate decision: do we have valid practice info? */
export function hasPracticeInfo(): boolean {
  return loadPracticeInfo().kind === "ok";
}
